import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/* ============================================================
   GUIDE — OPTIONS POIDS
   ============================================================

   Génère une liste de valeurs (en grammes) utilisées
   dans le sélecteur lorsque le produit est vendu au poids.
*/
const gramsOptions = (min = 100, max = 2000, step = 100) => {
  const arr = [];
  for (let g = min; g <= max; g += step) arr.push(g);
  return arr;
};

/* ============================================================
   GUIDE — OPTIONS UNITÉS
   ============================================================

   Génère une liste d’unités (1,2,3...) pour les produits
   vendus à la pièce ou équivalent.
*/
const piecesOptions = (min = 1, max = 10) => {
  const arr = [];
  for (let n = min; n <= max; n += 1) arr.push(n);
  return arr;
};

/* ============================================================
   GUIDE — FORMAT PRIX CHF
   ============================================================

   Accepte centimes ou francs, puis affiche un prix lisible.
*/
const formatCHFLocal = (value) => {
  const n = Number(value || 0);
  const francs = Number.isInteger(n) && n >= 100 ? n / 100 : n;
  return `${francs.toFixed(2)} CHF`;
};

/* ============================================================
   GUIDE — NORMALISATION TEXTE
   ============================================================ */
const normalizeUnitText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/* ============================================================
   GUIDE — DÉTECTION TYPE UNITÉ
   ============================================================ */
const isPieceUnit = (unite) => {
  const u = normalizeUnitText(unite);
  return (
    u.includes("pièce") ||
    u.includes("piece") ||
    u.includes("bouteille") ||
    u.includes("pot") ||
    u.includes("boîte") ||
    u.includes("boite") ||
    u.includes("bocal") ||
    u.includes("flacon") ||
    u.includes("barquette") ||
    u.includes("pack")
  );
};

/* ============================================================
   GUIDE — EXTRACTION POIDS FIXE
   ============================================================ */
const parseFixedGramsFromUnit = (unite) => {
  const u = normalizeUnitText(unite).replace(",", ".");
  const m = u.match(/(\d+(\.\d+)?)\s*(kg|g)/);
  if (!m) return null;

  const value = Number(m[1]);
  return m[3] === "kg" ? value * 1000 : value;
};

/* ============================================================
   GUIDE — LABEL UNITÉ
   ============================================================ */
const getDisplayUnitLabel = (unite) => {
  const u = normalizeUnitText(unite);
  if (u.includes("bouteille")) return "bouteille";
  if (u.includes("pot")) return "pot";
  if (u.includes("boîte") || u.includes("boite")) return "boîte";
  if (u.includes("bocal")) return "bocal";
  if (u.includes("flacon")) return "flacon";
  if (u.includes("barquette")) return "barquette";
  if (u.includes("pack")) return "pack";
  return "pièce";
};

const pluralizeDisplayUnit = (label, count) => {
  if (count <= 1) return label;
  if (label === "bocal") return "bocaux";
  if (label === "boîte") return "boîtes";
  return `${label}s`;
};

/* ============================================================
   GUIDE — NETTOYAGE VALEUR TEXTE
   ============================================================ */
const cleanValue = (value) => {
  const v = String(value || "").trim();
  return v || null;
};

/* ============================================================
   GUIDE — FAMILLE PRODUIT
   ============================================================

   Permet de générer les highlights selon le type produit :
   - fromage / laitier
   - cave
   - épicerie
*/
const getProductFamily = (product) => {
  const source = normalizeUnitText(
    `${product?.categorie || ""} ${product?.category || ""}`
  );

  if (
    source.includes("vin") ||
    source.includes("spiritueux") ||
    source.includes("eau de vie") ||
    source.includes("absinthe") ||
    source.includes("absenth") ||
    source.includes("cave")
  ) {
    return "cave";
  }

  if (source.includes("epicerie") || source.includes("épicerie")) {
    return "epicerie";
  }

  if (
    source.includes("fromage") ||
    source.includes("vache") ||
    source.includes("brebis") ||
    source.includes("chèvre") ||
    source.includes("chevre")
  ) {
    return "fromage";
  }

  return "default";
};

/* ============================================================
   GUIDE — LAIT ANIMAL
   ============================================================

   Priorité à la nouvelle colonne lait_animal.
   Fallback léger sur le nom uniquement.
*/
const getMilkAnimalFromProduct = (product) => {
  const explicit = normalizeUnitText(
    product?.lait_animal || product?.animal_lait || product?.milk_animal
  );

  if (explicit.includes("chèvre") || explicit.includes("chevre"))
    return "chèvre";
  if (explicit.includes("brebis")) return "brebis";
  if (explicit.includes("vache")) return "vache";
  if (explicit.includes("mixte")) return "mixte";

  return null;
};

/* ============================================================
   GUIDE — UNITÉ DE VENTE COURTE
   ============================================================ */
const getSaleUnitLabel = (product, unitLabel) => {
  const explicit =
    cleanValue(product?.saleUnitLabel) ||
    cleanValue(product?.sale_unit_label) ||
    cleanValue(product?.prix_unite) ||
    cleanValue(product?.priceUnit);

  if (explicit) return explicit;

  const fixed = parseFixedGramsFromUnit(unitLabel);
  if (fixed) return `les ${Math.round(fixed)}g`;

  if (!isPieceUnit(unitLabel)) return "les 100g";

  return getDisplayUnitLabel(unitLabel);
};

/* ============================================================
   GUIDE — ICÔNES SVG PREMIUM
   ============================================================ */
const MilkIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 3h6" />
    <path d="M10 3v4l-2 3v9.5A1.5 1.5 0 0 0 9.5 21h5A1.5 1.5 0 0 0 16 19.5V10l-2-3V3" />
    <path d="M8 13h8" />
    <path d="M10 17h4" />
  </svg>
);

const LeafIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 4c-7.5.6-12.5 4.2-14.2 10.2C4.7 18 7.8 20.9 11.6 19.6 17.5 17.6 20.2 11.8 20 4Z" />
    <path d="M6 18c3.2-4.6 7-7.8 11.4-9.6" />
    <path d="M10.2 13.8l-3.1-.2" />
    <path d="M13.5 10.9l-.3-3.2" />
  </svg>
);

const MountainIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 18.5 9.2 8l4.3 7.1" />
    <path d="M11.3 18.5 15.8 11 21 18.5Z" />
    <path d="M7.8 10.4l1.4 1.4 1.5-1.5" />
    <path d="M14.6 13l1.2 1.2 1.2-1.1" />
  </svg>
);

const BottleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M10 3h4" />
    <path d="M11 3v5l-2 3v8a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-8l-2-3V3" />
    <path d="M9 14h6" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 6h14v14H5z" />
    <path d="M8 3v5" />
    <path d="M16 3v5" />
    <path d="M5 11h14" />
  </svg>
);

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
    <path d="M18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8L18 15Z" />
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 9h10l1 11H6L7 9Z" />
    <path d="M9 9V7a3 3 0 0 1 6 0v2" />
    <path d="M9.5 13h5" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 5.5c2.7 0 5 .7 7 2.1v11c-2-1.4-4.3-2.1-7-2.1v-11Z" />
    <path d="M19 5.5c-2.7 0-5 .7-7 2.1v11c2-1.4 4.3-2.1 7-2.1v-11Z" />
  </svg>
);

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ProductQuickViewModal({
  isOpen,
  onClose,
  product,
  onAddToCart,
}) {
  const [grams, setGrams] = useState("");
  const [pieces, setPieces] = useState("");
  const [justAdded, setJustAdded] = useState(false);
  const [showStory, setShowStory] = useState(false);

  /* ============================================================
     GUIDE — RESET À L’OUVERTURE
     ============================================================ */
  useEffect(() => {
    if (!isOpen) return;
    setGrams("");
    setPieces("");
    setJustAdded(false);
    setShowStory(false);
  }, [isOpen, product?.id]);

  /* ============================================================
     GUIDE — LOCK BODY SCROLL
     ============================================================

     Empêche le scroll de la page derrière la modale.
     Utile surtout mobile.
  */
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [isOpen]);

  /* ============================================================
     GUIDE — CONFIG PRODUIT
     ============================================================ */
  const unitLabel = product?.unite || "pièce";

  const isUnitPiece = isPieceUnit(unitLabel);
  const fixedGrams = parseFixedGramsFromUnit(unitLabel);
  const isFixedGramUnit = !isUnitPiece && fixedGrams && fixedGrams > 0;
  const isWeightMode = !isUnitPiece && !isFixedGramUnit;

  const gramOptions = gramsOptions();
  const pieceOpts = piecesOptions();

  const storyText = product?.histoire || product?.storytelling || "";

  /* ============================================================
     GUIDE — PRIX + UNITÉ DE VENTE
     ============================================================ */
  const displayPrice =
    product?.displayPrice ?? product?.appliedPrice ?? product?.price ?? 0;

  const saleUnitLabel = getSaleUnitLabel(product, unitLabel);

  /* ============================================================
     GUIDE — BADGE TYPE DE LAIT
     ============================================================

     Affiche pasteurisé / thermisé / lait cru.
  */
  const milkTreatmentLabel = cleanValue(product?.type_lait);

  /* ============================================================
     GUIDE — HIGHLIGHTS PREMIUM PAR FAMILLE PRODUIT
     ============================================================

     Fromage / laitier :
     - lait animal
     - affinage
     - origine

     Cave :
     - catégorie cave
     - millésime
     - région / cépage

     Épicerie :
     - origine
     - texture / intensité / conseil
  */
  const highlights = useMemo(() => {
    const items = [];
    const family = getProductFamily(product);

    const categorie = cleanValue(product?.categorie || product?.category);
    const origine = cleanValue(product?.origine);
    const affinage = cleanValue(product?.affinage);
    const region = cleanValue(product?.region);
    const cepage = cleanValue(product?.cepage);
    const millesime = cleanValue(product?.millesime);
    const texture = cleanValue(product?.texture);
    const intensite = cleanValue(product?.intensite);
    const conseil = cleanValue(product?.conseil_degustation);

    if (family === "fromage") {
      const milkAnimal = getMilkAnimalFromProduct(product);

      if (milkAnimal) {
        items.push({
          key: "milk",
          icon: <MilkIcon />,
          label: `Lait de ${milkAnimal}`,
        });
      }

      if (affinage) {
        items.push({
          key: "aging",
          icon: <LeafIcon />,
          label:
            affinage.toLowerCase().includes("affiné") ||
            affinage.toLowerCase().includes("affine")
              ? affinage
              : `Affiné ${affinage}`,
        });
      }

      if (origine) {
        items.push({
          key: "origin",
          icon: <MountainIcon />,
          label: origine.toLowerCase().includes("origine")
            ? origine
            : `Origine ${origine}`,
        });
      }

      return items.slice(0, 3);
    }

    if (family === "cave") {
      if (categorie) {
        items.push({
          key: "cave-type",
          icon: <BottleIcon />,
          label: categorie,
        });
      }

      if (millesime) {
        items.push({
          key: "millesime",
          icon: <CalendarIcon />,
          label: `Millésime ${millesime}`,
        });
      }

      if (region || cepage) {
        items.push({
          key: "region-cepage",
          icon: <MountainIcon />,
          label: region || cepage,
        });
      }

      return items.slice(0, 3);
    }

    if (family === "epicerie") {
      if (origine) {
        items.push({
          key: "origin",
          icon: <MountainIcon />,
          label: `Origine ${origine}`,
        });
      }

      if (texture) {
        items.push({
          key: "texture",
          icon: <LeafIcon />,
          label: texture,
        });
      }

      if (intensite || conseil) {
        items.push({
          key: "intensity",
          icon: <SparkIcon />,
          label: intensite || conseil,
        });
      }

      return items.slice(0, 3);
    }

    return items;
  }, [product]);

  const hasSelectedQuantity = isWeightMode ? Boolean(grams) : Boolean(pieces);

  /* ============================================================
     GUIDE — AJOUT PANIER
     ============================================================ */
  const handleAdd = () => {
    if (justAdded || !hasSelectedQuantity) return;

    if (isWeightMode && grams) {
      onAddToCart?.({
        productId: product.id,
        name: product.name,
        imageUrl: product.imageUrl,
        grams: Number(grams),
        priceCents: product.price ?? 0,
      });
    } else if (isFixedGramUnit && pieces) {
      onAddToCart?.({
        productId: product.id,
        name: product.name,
        imageUrl: product.imageUrl,
        pieces: Number(pieces),
        fixedUnitGrams: fixedGrams,
        priceCents: product.price ?? 0,
      });
    } else if (pieces) {
      onAddToCart?.({
        productId: product.id,
        name: product.name,
        imageUrl: product.imageUrl,
        pieces: Number(pieces),
        priceCents: product.price ?? 0,
      });
    }

    setJustAdded(true);

    setTimeout(() => {
      onClose?.();
    }, 850);
  };

  if (!isOpen || !product) return null;

  const modalMarkup = (
    <div className="mdl-overlay" onClick={onClose}>
  <div className="mdl-scale-wrap">
    <div className="mdl-card" onClick={(e) => e.stopPropagation()}>
        {/* =====================================================
            BLOC IMAGE
            -----------------------------------------------------
            Affiche l’image produit sans logique interactive.
        ===================================================== */}
        {!showStory && (
          <div className="mdl-imgCol">
            <img src={product.imageUrl} className="mdl-img" alt={product.name} />
          </div>
        )}

        {/* =====================================================
            BLOC CONTENU
        ===================================================== */}
        <div className="mdl-infoCol">
          {/* =====================================================
              TITRE + BADGE
              -----------------------------------------------------
              Badge = type_lait pour les fromages.
          ===================================================== */}
          {!showStory && (
            <div className="mdl-title-row">
              <div>
                {milkTreatmentLabel ? (
                  <span className="mdl-milk">{milkTreatmentLabel}</span>
                ) : null}

                <h2 className="mdl-title">{product.name}</h2>

                <div className="mdl-price-row">
                  <strong>{formatCHFLocal(displayPrice)}</strong>
                  <span>{saleUnitLabel}</span>
                </div>
              </div>
            </div>
          )}

          {/* =====================================================
              BLOC SÉLECTION QUANTITÉ
              -----------------------------------------------------
              Select premium avec icône dorée à gauche.
          ===================================================== */}
          {!showStory && (
            <div className="mdl-block">
              <div className="mdl-select-wrap">
                <span className="mdl-select-icon">
                  <BagIcon />
                </span>

                <select
                  className="mdl-select"
                  value={isWeightMode ? grams : pieces}
                  onChange={(e) =>
                    isWeightMode
                      ? setGrams(e.target.value)
                      : setPieces(e.target.value)
                  }
                >
                  <option value="">Choisir une quantité</option>

                  {isWeightMode
                    ? gramOptions.map((g) => (
                        <option key={g} value={g}>
                          {g} g
                        </option>
                      ))
                    : pieceOpts.map((n) => (
                        <option key={n} value={n}>
                          {isFixedGramUnit
                            ? `${n} × ${unitLabel}`
                            : `${n} ${pluralizeDisplayUnit(
                                getDisplayUnitLabel(unitLabel),
                                n
                              )}`}
                        </option>
                      ))}
                </select>
              </div>

              {/* =====================================================
                  ACTIONS PRINCIPALES
              ===================================================== */}
              <div className="mdl-actions">
                <button
                  className={`mdl-primary ${justAdded ? "is-added" : ""}`}
                  onClick={handleAdd}
                  disabled={!hasSelectedQuantity || justAdded}
                  type="button"
                >
                  {justAdded ? "✓ AJOUTÉ" : "Ajouter au panier"}
                </button>

                <button
                  className="mdl-secondary"
                  onClick={onClose}
                  type="button"
                >
                  Fermer
                </button>
              </div>

              {/* =====================================================
                  HIGHLIGHTS PRODUIT
                  -----------------------------------------------------
                  Affiche les infos premium selon la famille produit.
              ===================================================== */}
              {highlights.length > 0 && (
                <div className="mdl-highlights">
                  {highlights.map((item) => (
                    <div className="mdl-highlight" key={item.key}>
                      <div className="mdl-highlight-icon">{item.icon}</div>
                      <div className="mdl-highlight-label">{item.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* =====================================================
                  BOUTON STORYTELLING
                  -----------------------------------------------------
                  Carte d’accès à l’histoire produit.
              ===================================================== */}
              {storyText ? (
                <button
                  className="mdl-discover-card"
                  onClick={() => setShowStory(true)}
                  type="button"
                >
                  <span className="mdl-discover-icon">
                    <BookIcon />
                  </span>

                  <span className="mdl-discover-text">
                    <strong>Une histoire de maquis et de tradition</strong>
                    <span>Découvrir son histoire</span>
                  </span>

                  <span className="mdl-discover-arrow">›</span>
                </button>
              ) : null}
            </div>
          )}

          {/* =====================================================
              STORYTELLING
              -----------------------------------------------------
              Vue dédiée à la lecture de l'histoire produit.
          ===================================================== */}
          {showStory && (
            <div className="mdl-story-panel">
              <button
                className="mdl-back"
                onClick={() => setShowStory(false)}
                type="button"
              >
                ← Retour
              </button>

              <div className="mdl-story-kicker">L’histoire</div>

              <div className="mdl-story-title">{product.name}</div>

              <div className="mdl-story">{storyText}</div>

              {product?.conseil_degustation ? (
                <div className="mdl-tasting">
                  <strong>Conseil dégustation</strong>
                  <span>{product.conseil_degustation}</span>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ============================================================
         STYLE LOCAL MODAL
         ============================================================ */}
<style jsx="true">{`
  .mdl-overlay {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    background: rgba(0, 0, 0, 0.62);
    display: grid;
    place-items: center;
    z-index: 2147483647;
    padding: 10px; 
    overflow: hidden;
    isolation: isolate;
  }

  .mdl-card {
    width: min(260px, 68vw);
    max-height: 85dvh;
    background: linear-gradient(180deg, #fffaf2, #f6efe3);
    border: 1px solid rgba(176, 138, 69, 0.28);
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 15px 45px rgba(0, 0, 0, 0.3);
    transform-origin: center center;
  }

  .mdl-imgCol {
    width: 100%;
    background: #f6efe3;
  }

  .mdl-img {
    width: 100%;
    aspect-ratio: 1.1 / 1;
    object-fit: cover;
    display: block;
  }

  .mdl-infoCol {
    padding: 10px 12px;
  }

  .mdl-title {
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 1.1;
    letter-spacing: 0.04em;
    font-family: "Playfair Display", Georgia, serif;
    font-weight: 550;
    color: #24211c;
    text-transform: uppercase;
  }

  .mdl-milk {
    display: inline-flex;
    width: fit-content;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(176, 138, 69, 0.11);
    color: #9a742d;
    font-size: 7px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .mdl-price-row {
    margin-top: 4px;
    display: flex;
    align-items: baseline;
    gap: 5px;
    color: #1f1d1a;
  }

  .mdl-price-row strong {
    font-size: 12px;
    line-height: 1;
    font-weight: 800;
  }

  .mdl-price-row span {
    font-size: 9px;
    color: rgba(0, 0, 0, 0.56);
    font-weight: 700;
  }

  .mdl-block {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(43, 36, 29, 0.1);
  }

  .mdl-select-wrap {
    position: relative;
    width: 100%;
  }

  .mdl-select-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    width: 14px;
    height: 14px;
    transform: translateY(-50%);
    color: #a37a2d;
    pointer-events: none;
    z-index: 2;
  }

  .mdl-select-icon svg,
  .mdl-highlight-icon svg,
  .mdl-discover-icon svg {
    width: 100%;
    height: 100%;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .mdl-select {
    width: 100%;
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(43, 36, 29, 0.15);
    background: #fff;
    padding: 0 8px 0 30px;
    font-size: 11px;
    font-weight: 700;
    color: #211f1b;
    outline: none;
    appearance: auto;
  }

  .mdl-actions {
    display: grid;
    grid-template-columns: 1fr 0.7fr;
    gap: 6px;
    margin-top: 8px;
  }

  .mdl-primary,
  .mdl-secondary {
    height: 32px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
  }

  .mdl-primary {
    border: none;
    background: #111;
    color: #fff;
  }

  .mdl-primary:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .mdl-primary.is-added {
    background: #b08a45;
  }

  .mdl-secondary {
    background: #fff;
    color: #222;
    border: 1px solid #ddd;
  }

  .mdl-highlights {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(43, 36, 29, 0.1);
    display: flex;
    justify-content: space-between;
    gap: 4px;
  }

  .mdl-highlight {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    flex: 1;
  }

  .mdl-highlight-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #fff;
    border: 0.5px solid rgba(176, 138, 69, 0.2);
    color: #a37a2d;
    padding: 6px;
  }

  .mdl-highlight-label {
    font-size: 9px;
    line-height: 1.2;
    font-weight: 800;
    color: rgba(36, 33, 28, 0.86);
    text-align: center;
    white-space: nowrap;
  }

  .mdl-discover-card {
    width: 100%;
    margin-top: 10px;
    padding: 8px;
    border-radius: 12px;
    border: 1px solid rgba(176, 138, 69, 0.13);
    background: rgba(255, 255, 255, 0.5);
    display: grid;
    grid-template-columns: 28px 1fr 10px;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    text-align: left;
  }

  .mdl-discover-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #fff;
    border: 1px solid rgba(176, 138, 69, 0.14);
    color: #a37a2d;
    padding: 6px;
  }

  .mdl-discover-text {
    display: grid;
    gap: 2px;
  }

  .mdl-discover-text strong {
    font-family: "Playfair Display", Georgia, serif;
    font-size: 11px;
    line-height: 1.1;
    font-weight: 600;
    color: #29251f;
  }

  .mdl-discover-text span {
    color: #9a742d;
    font-size: 9px;
    font-weight: 800;
  }

  /* --- STORYTELLING RÉTABLI --- */
  .mdl-discover-arrow {
    color: #9a742d;
    font-size: 30px; /* Retour original */
    line-height: 1;
  }

  .mdl-story-panel {
    max-height: 78dvh; /* Retour original */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 12px 12px 40px 12px;
  }

  .mdl-back {
    border: none;
    background: transparent;
    color: #8b6a2e;
    font-weight: 800;
    font-size: 13px; /* Retour original */
    padding: 0;
    cursor: pointer;
    margin-bottom: 18px; /* Retour original */
  }

  .mdl-story-kicker {
    text-align: center;
    color: #9a742d;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .mdl-story-title {
    font-family: "Playfair Display", Georgia, serif;
    font-size: 18px;
    line-height: 0.8;
    text-align: center;
    color: #24211c;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .mdl-story {
  font-family: "Playfair Display", Georgia, serif;
      white-space: pre-line;
    font-size: 14px;
    line-height: 1.25;
    color: rgba(30, 28, 25, 0.92);
  }

  .mdl-tasting {
    margin-top: 12px;
    padding: 19px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.44);
    border: 1px solid rgba(176, 138, 69, 0.13);
    display: grid;
    gap: 6px;
  }

  .mdl-tasting strong {
    color: #9a742d;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .mdl-tasting span {
    font-size: 11px;
    line-height: 1.45;
    color: rgba(36, 33, 28, 0.82);
  }

  @media (max-width: 520px) {
  .mdl-overlay {
    padding: 10px;
  }
  
  .mdl-card {
    width: 60vw;
    max-height: 85dvh;
  }

  .mdl-story-panel {
    /* Ajusté pour laisser de la place au contenu dans la carte */
    max-height: calc(85dvh - 20px);
    /* Augmentation du padding-bottom (60px) pour que la dégustation remonte bien au scroll */
    padding: 10px 10px 60px 10px; 
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* --- CORRECTION TASTING MOBILE --- */
  .mdl-tasting {
    margin-top: 15px;
    margin-bottom: 20px; /* Sécurité supplémentaire en fin de liste */
    padding: 10px;
    gap: 4px;
    border-radius: 12px;
  }

  .mdl-tasting strong {
    font-size: 9px;
    letter-spacing: 0.1em;
  }

  .mdl-tasting span {
    font-size: 11px;
    line-height: 1.3;
  }

  /* Correction du titre (taille et espacement des lignes) */
  .mdl-story-title {
    font-size: 18px;
    line-height: 1.1; /* Augmenté pour éviter le chevauchement */
    margin-bottom: 12px;
  }
} 
  }
`}</style>
    </div>
  );

  return createPortal(modalMarkup, document.body);
}